import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, DollarSign, MapPin, Upload } from 'lucide-react';
import axios from 'axios';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { maskCurrencyBRL, maskPhone } from '../../../shared/utils/validators';
import { currencyInputToApi } from '../../recrutamento/components/format';
import { validateInscricaoForm } from '../../recrutamento/validation/recrutamentoForms';
import { PORTAL_KEYS, buscarVagaPublica, inscrever, type ApplyPayload } from '../api';

const WORK_MODEL_LABELS: Record<string, string> = {
  presencial: 'Presencial',
  hibrido: 'Híbrido',
  remoto: 'Remoto',
};

/**
 * Detalhe da vaga e formulário de inscrição.
 *
 * Currículo e aceite dos termos são obrigatórios — o servidor recusa sem
 * eles, e a validação aqui evita que o candidato preencha tudo para
 * descobrir isso no envio. O token do captcha, quando o provedor está
 * ativo, é injetado pelo widget na variável global; sem provedor
 * configurado o campo simplesmente não existe.
 */
export default function VagaPublicaPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const { data: vaga, isLoading, isError } = useQuery({
    queryKey: PORTAL_KEYS.vaga(id ?? 'noop'),
    queryFn: () => buscarVagaPublica(id as string),
    enabled: Boolean(id),
  });

  const [values, setValues] = useState<Partial<ApplyPayload>>({ consent: false });
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [protocolo, setProtocolo] = useState<string | null>(null);

  const inscricao = useMutation({
    mutationFn: (payload: { data: ApplyPayload; file: File }) =>
      inscrever(id as string, payload.data, payload.file),
    onSuccess: (result) => setProtocolo(result.protocol),
  });

  function setField<K extends keyof ApplyPayload>(key: K, value: ApplyPayload[K]) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: '' }));
  }

  function validar(): boolean {
    // Mesmo módulo puro do cadastro interno, com as duas exigências
    // extras do portal: currículo e aceite.
    const next = validateInscricaoForm({
      fullName: values.fullName,
      cpf: values.cpf,
      email: values.email,
      phone: values.phone,
      expectedSalary: currencyInputToApi(values.expectedSalary),
      consent: values.consent,
      hasFile: file !== null,
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  /** Mensagem do servidor traduzida para o que o candidato precisa fazer. */
  function mensagemErro(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 409) return 'Você já possui inscrição ativa nesta vaga.';
      if (status === 404) return 'Esta vaga não está mais disponível.';
      if (status === 429) return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
      if (status === 413) return 'O currículo excede o tamanho máximo de 10 MB.';
      const detail = (error.response?.data as { message?: string | string[] })?.message;
      if (Array.isArray(detail)) return detail[0];
      if (detail) return detail;
    }
    return 'Não foi possível concluir a inscrição. Tente novamente.';
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-3xl space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !vaga) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm font-medium text-gray-900">Vaga não disponível</p>
          <p className="mt-1 text-sm text-gray-500">
            Ela pode ter sido encerrada ou preenchida.
          </p>
          <Link to="/vagas" className="mt-4 inline-block text-sm text-blue-600 hover:underline">
            Ver vagas abertas
          </Link>
        </div>
      </div>
    );
  }

  if (protocolo) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-8 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
          <h1 className="mt-3 text-lg font-semibold text-gray-900">Inscrição confirmada</h1>
          <p className="mt-2 text-sm text-gray-600">
            Seu protocolo é <strong>{protocolo}</strong>. Enviamos a confirmação para{' '}
            {values.email}.
          </p>
          <Link
            to="/vagas/acompanhar"
            className="mt-4 inline-block text-sm text-blue-600 hover:underline"
          >
            Acompanhar minha candidatura
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Link to="/vagas" className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600">
          <ArrowLeft className="h-4 w-4" /> Todas as vagas
        </Link>

        <article className="rounded-lg border border-gray-200 bg-white p-6">
          <h1 className="text-xl font-bold text-gray-900">{vaga.title}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {vaga.city && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {vaga.city}
              </span>
            )}
            {vaga.workModel && <span>{WORK_MODEL_LABELS[vaga.workModel]}</span>}
            {vaga.deadline && (
              <span>Inscrições até {new Date(vaga.deadline).toLocaleDateString('pt-BR')}</span>
            )}
          </p>

          <div className="mt-4 whitespace-pre-wrap text-sm text-gray-700">{vaga.description}</div>

          {vaga.requirements && (
            <>
              <h2 className="mt-6 text-sm font-semibold text-gray-900">Requisitos</h2>
              <div className="mt-1 whitespace-pre-wrap text-sm text-gray-700">
                {vaga.requirements}
              </div>
            </>
          )}
        </article>

        <form
          className="mt-6 rounded-lg border border-gray-200 bg-white p-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (!validar() || !file) return;
            inscricao.mutate({
              data: {
                fullName: values.fullName!.trim(),
                cpf: values.cpf!.trim(),
                email: values.email!.trim(),
                phone: values.phone!.trim(),
                city: values.city?.trim(),
                expectedSalary: currencyInputToApi(values.expectedSalary),
                linkedinUrl: values.linkedinUrl?.trim(),
                consent: true,
                captchaToken:
                  (window as unknown as { __nexosCaptchaToken?: string }).__nexosCaptchaToken,
              },
              file,
            });
          }}
        >
          <h2 className="text-base font-semibold text-gray-900">Inscreva-se</h2>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <TextInput
                label="Nome completo"
                value={values.fullName ?? ''}
                error={errors.fullName}
                onChange={(event) => setField('fullName', event.target.value)}
              />
            </div>
            <TextInput
              label="CPF"
              value={values.cpf ?? ''}
              error={errors.cpf}
              onChange={(event) => setField('cpf', event.target.value)}
            />
            <TextInput
              label="Telefone"
              inputMode="tel"
              value={values.phone ?? ''}
              error={errors.phone}
              onChange={(event) => setField('phone', maskPhone(event.target.value))}
            />
            <TextInput
              label="E-mail"
              type="email"
              value={values.email ?? ''}
              error={errors.email}
              onChange={(event) => setField('email', event.target.value)}
            />
            <TextInput
              label="Cidade"
              value={values.city ?? ''}
              onChange={(event) => setField('city', event.target.value)}
            />
            <TextInput
              label="Pretensão salarial"
              inputMode="numeric"
              icon={<DollarSign className="h-4 w-4" />}
              value={values.expectedSalary ?? ''}
              error={errors.expectedSalary}
              onChange={(event) => setField('expectedSalary', maskCurrencyBRL(event.target.value))}
            />
            <TextInput
              label="LinkedIn"
              value={values.linkedinUrl ?? ''}
              onChange={(event) => setField('linkedinUrl', event.target.value)}
            />
          </div>

          {/*
            Seletor de arquivo no padrão do app (input escondido + botão
            do design system, como em CompanyRegistrationForm e
            MeusDocumentosPage) — o controle nativo não tem equivalente
            compartilhado e destoa do resto do formulário.
          */}
          <div className="mt-4">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Currículo (PDF, DOC ou DOCX, até 10 MB)
            </span>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => fileInputRef.current?.click()}
              >
                {file ? 'Trocar arquivo' : 'Selecionar currículo'}
              </Button>
              <span className="text-sm text-gray-600">
                {file ? file.name : 'Nenhum arquivo selecionado'}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setErrors((previous) => ({ ...previous, file: '' }));
              }}
            />
            {errors.file && <p className="mt-1 text-xs text-red-600">{errors.file}</p>}
          </div>

          <div className="mt-4 flex items-start gap-2">
            <input
              id="consent"
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={values.consent ?? false}
              onChange={(event) => setField('consent', event.target.checked)}
            />
            <label htmlFor="consent" className="text-sm text-gray-700">
              Autorizo o tratamento dos meus dados para este processo seletivo e a retenção do meu
              currículo no banco de talentos para vagas futuras. Posso solicitar a exclusão a
              qualquer momento.
            </label>
          </div>
          {errors.consent && <p className="mt-1 text-xs text-red-600">{errors.consent}</p>}

          {inscricao.isError && (
            <Alert variant="error" className="mt-4">
              {mensagemErro(inscricao.error)}
            </Alert>
          )}

          <Button type="submit" loading={inscricao.isPending} className="mt-6 w-full">
            Enviar inscrição
          </Button>
        </form>
      </main>
    </div>
  );
}

